import { useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { Plus, Radar, TriangleAlert } from 'lucide-react';
import { Button, DataTable, EmptyState, ErrorState, FormField, PageHeader, SearchPanel, Select, StatusBadge, TextField, type Column } from '@/components';
import { useCurrentUser } from '@/auth/AuthProvider';
import { useFiscalYear } from '@/fiscal-year/FiscalYearProvider';
import { formatDate } from '@/lib/format';
import { useCodeValues } from '@/features/masters/api';
import { BusinessOperatorSelect } from '@/features/masters/components/BusinessOperatorSelect';
import { useEquipmentList, type EquipmentDto, type EquipmentFilter } from './api';

function useFilterFromUrl(): [EquipmentFilter, (f: EquipmentFilter) => void] {
  const [params, setParams] = useSearchParams();
  const filter = useMemo<EquipmentFilter>(
    () => ({
      businessOperatorId: params.get('businessOperatorId') ?? '',
      category: params.get('category') ?? '',
      fiscalYear: params.get('fiscalYear') ?? '',
      identificationNo: params.get('identificationNo') ?? '',
      vesselName: params.get('vesselName') ?? '',
    }),
    [params],
  );
  const setFilter = (f: EquipmentFilter) => {
    const next = new URLSearchParams();
    Object.entries(f).forEach(([k, v]) => v && next.set(k, String(v)));
    setParams(next, { replace: true });
  };
  return [filter, setFilter];
}

/** S-80 機器管理一覧（基金：登録・編集／漁協：自組織の参照） */
export function EquipmentListPage() {
  const navigate = useNavigate();
  const user = useCurrentUser();
  const { year, years, yearPath } = useFiscalYear();
  const { data: codes } = useCodeValues();
  const [filter, setFilter] = useFilterFromUrl();
  const { data, isPending, isFetching, error, refetch } = useEquipmentList(filter);
  const { register, handleSubmit, reset, setValue, watch } = useForm<EquipmentFilter>({ values: filter });
  const isAdmin = user.organizationType === '基金';

  const columns: Column<EquipmentDto>[] = [
    { key: 'managementNo', header: '管理番号', numeric: true, sortable: true, width: 90 },
    { key: 'businessOperatorName', header: '事業実施者', sortable: true, width: 170 },
    { key: 'category', header: '機器区分', sortable: true, width: 100, render: (e) => <StatusBadge tone="navy">{e.category}</StatusBadge> },
    { key: 'fiscalYear', header: '年度', numeric: true, sortable: true, width: 80, render: (e) => e.fiscalYear ?? '—' },
    { key: 'vesselName', header: '船名', sortable: true, width: 150, render: (e) => e.vesselName ?? e.nonParticipantVesselName ?? '—' },
    { key: 'registrationNo', header: '漁船登録番号', width: 120, render: (e) => <span className="bs-num">{e.registrationNo ?? '—'}</span> },
    {
      key: 'identificationNo',
      header: '識別番号',
      width: 200,
      sortable: true,
      render: (e) => (
        <span>
          <span className="bs-num">{e.identificationNo ?? '—'}</span>
          {e.isDuplicate && <StatusBadge tone="danger" dot>＊重複有り</StatusBadge>}
        </span>
      ),
    },
    { key: 'maker', header: 'メーカー・型番', width: 160, render: (e) => [e.maker, e.modelNo].filter(Boolean).join(' ') || '—' },
    { key: 'lendingFrom', header: '貸出期間', width: 130, render: (e) => (e.lendingFrom || e.lendingTo ? <span className="bs-num" style={{ fontSize: 12 }}>{formatDate(e.lendingFrom) || '—'}〜<br />{formatDate(e.lendingTo) || '—'}</span> : '—') },
  ];

  const operatorId = watch('businessOperatorId');
  const dup = data?.duplicates;
  const dupCount = (dup?.identificationNos.length ?? 0) + (dup?.registrationNos.length ?? 0);
  const summary = [filter.businessOperatorId && `事業実施者ID: ${filter.businessOperatorId}`, filter.category, filter.fiscalYear && `${filter.fiscalYear}年度`, filter.identificationNo && `識別番号: ${filter.identificationNo}`, filter.vesselName && `船名: ${filter.vesselName}`]
    .filter(Boolean)
    .join(' ／ ');

  return (
    <>
      <PageHeader
        eyebrow="船舶・計画"
        title="機器管理"
        description={isAdmin ? 'AIS・船舶電話・EPIRB・PLB など、貸与機器の台帳です。' : '自組織の機器台帳を参照できます（登録・変更は基金が行います）。'}
        actions={
          isAdmin && (
            <Button variant="primary" icon={<Plus size={16} />} onClick={() => navigate(yearPath('equipment/new'))}>
              新規登録
            </Button>
          )
        }
      />
      <SearchPanel
        onSubmit={handleSubmit((v) => setFilter(v))}
        onReset={() => {
          reset({ businessOperatorId: '', category: '', fiscalYear: '', identificationNo: '', vesselName: '' });
          setFilter({});
        }}
        summary={summary || '条件なし'}
        searching={isFetching}
        columns={5}
      >
        <FormField label="事業実施者">
          <BusinessOperatorSelect fiscalYear={year} value={operatorId ? Number(operatorId) : null} onChange={(v) => setValue('businessOperatorId', v == null ? '' : String(v))} />
        </FormField>
        <FormField label="機器区分">
          <Select placeholder="すべて" options={(codes?.equipmentCategories ?? []).map((v) => ({ value: v, label: v }))} {...register('category')} />
        </FormField>
        <FormField label="年度">
          <Select placeholder="すべて" options={years.map((y) => ({ value: y.year, label: `${y.label}` }))} {...register('fiscalYear')} />
        </FormField>
        <FormField label="識別番号">
          <TextField {...register('identificationNo')} />
        </FormField>
        <FormField label="船名">
          <TextField {...register('vesselName')} />
        </FormField>
      </SearchPanel>

      {dupCount > 0 && (
        <div className="bs-readonly-note" role="status" style={{ borderLeftColor: 'var(--danger-600)', background: 'var(--danger-100)' }}>
          <TriangleAlert size={15} style={{ verticalAlign: -2, marginRight: 6 }} aria-hidden />
          ＊重複有り：識別番号または漁船登録番号が一覧内で重複しています（{[...(dup?.identificationNos ?? []), ...(dup?.registrationNos ?? [])].join('、')}）。
        </div>
      )}

      {error ? (
        <ErrorState error={error} onRetry={() => void refetch()} />
      ) : (
        <DataTable
          caption="機器一覧"
          columns={columns}
          rows={data?.items ?? []}
          rowKey={(e) => e.id}
          loading={isPending}
          defaultSort={{ key: 'managementNo', direction: 'asc' }}
          pagination={{ pageSize: 25 }}
          dense
          onRowClick={(e) => navigate(yearPath(`equipment/${e.id}`))}
          empty={<EmptyState compact icon={<Radar />} title="該当する機器はありません" description="検索条件を変更してください。" />}
        />
      )}
    </>
  );
}
